import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import AutocompleteInput from "components/shared/AutocompleteInput";
import Input from "components/shared/Input";
import Modal from "components/shared/Modal";
import ResponsiveSelect from "components/shared/ResponsiveSelect";

const propTypes = {
  afterSubmit: PropTypes.func,
  logoUrl: PropTypes.string.isRequired,
  showAfterDelay: PropTypes.number,
  showOnMount: PropTypes.bool,
  successMessage: PropTypes.string
};

const defaultProps = {
  afterSubmit: null,
  showAfterDelay: null,
  showOnMount: false,
  /* eslint-disable max-len */
  successMessage:
    "While we can't cover every request we receive, we pay close attention to requests, and the requests we get do influence which titles we cover."
  /* eslint-enable max-len */
};

const categoryOptionsLarge = [
  {
    label: "A new literature guide",
    value: "requests"
  },
  {
    label: "A new poetry guide",
    value: "poetry_guide_requests"
  }
];

const categoryOptionsSmall = [
  {
    label: "Lit guide",
    value: "requests"
  },
  {
    label: "Poetry guide",
    value: "poetry_guide_requests"
  }
];

const Title = ({ src }) => <img src={src} alt="logo" />;

const initialFormState = {};

const RequestContentModal = (props) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [category, setCategory] = useState();
  const [formState, setFormState] = useState(initialFormState);
  const [showError, setShowError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [coveredResource, setCoveredResource] = useState({});

  useEffect(() => {
    if (
      (formState.requested_book &&
        formState.author_name &&
        formState.requested_book.length &&
        formState.author_name.length) ||
      (formState.requested_poetry_guide &&
        formState.author_name &&
        formState.requested_poetry_guide.length &&
        formState.author_name.length)
    ) {
      setCanSubmit(true);
    }
  }, [formState]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const Footer = () =>
    submitted ? (
      <>
        <div className="tw-hidden sm:tw-block">
          <button
            className="btn btn-default btn-md"
            onClick={resetForm}
            type="button"
          >
            Request another guide
          </button>
          <button
            className="btn btn-primary btn-md"
            data-dismiss="modal"
            type="button"
          >
            Close
          </button>
        </div>
        <div className="sm:tw-hidden">
          <button
            className="btn btn-primary btn-md"
            data-dismiss="modal"
            type="button"
          >
            Close
          </button>
          <button
            className="btn btn-default btn-md"
            onClick={resetForm}
            type="button"
          >
            Request another guide
          </button>
        </div>
      </>
    ) : (
      <>
        <button
          className="btn btn-default btn-md"
          data-dismiss="modal"
          type="button"
        >
          Cancel
        </button>
        <button
          className="btn btn-primary btn-md"
          disabled={!canSubmit}
          onClick={onSubmit}
          form="content-request-form"
          type="submit"
        >
          Submit
        </button>
      </>
    );

  const onSubmit = (e) => {
    e.preventDefault();

    setCanSubmit(false);
    setShowError(false);

    $.ajax({
      data: { contact_inquiry: { category, ...formState } },
      type: "post",
      url: "/contact-content-request.json"
    })
      .done((data) => {
        const { status, title, url, votes, rank } = data;
        setCoveredResource({ rank, title, url, votes });
        setSubmitted(status);
        if (props.afterSubmit) {
          props.afterSubmit();
        }
      })
      .fail(() => {
        setCanSubmit(true);
        setShowError(true);
      });
  };

  const onInputChange = (e) => {
    setCanSubmit(false);

    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  const onTitleChange = (e, { newValue }) => {
    setCanSubmit(false);

    setFormState({
      ...formState,
      [requestedFieldNames[category]]: newValue
    });
  };

  const onAuthorChange = (e, { newValue }) => {
    setCanSubmit(false);

    setFormState({
      ...formState,
      author_name: newValue
    });
  };

  const onTitleAutoselect = (suggestion) => {
    setFormState({
      ...formState,
      [requestedFieldNames[category]]: suggestion.title,
      author_name: suggestion.author
    });
  };

  const onAuthorAutoselect = (suggestion) => {
    setFormState({
      ...formState,
      author_name: suggestion.author
    });
  };

  const handleCategoryChange = (selectedCategory) => {
    setFormState({}); // Clear form state to not submit unused fields
    setCanSubmit(false);
    setCategory(selectedCategory);
  };

  const resetForm = () => {
    setFormState({});
    setCategory();
    setSubmitted(false);
    setCanSubmit(false);
  };

  const inputs = {
    poetry_guide_requests: (
      <Input
        handleChange={onInputChange}
        inputClass="input-md"
        InputComponent="textarea"
        inputId="contact_inquiry_message"
        isControlled
        labelText="Why should we cover this poem? (optional)"
        key="poetry_request_message"
        name="message"
        placeholder="Type message here"
        value={formState.message}
      />
    ),
    requests: (
      <Input
        inputClass="input-md"
        InputComponent="textarea"
        inputId="contact_inquiry_message"
        isControlled
        labelText="Why should we cover this title? (optional)"
        key="request_message"
        name="message"
        placeholder="Type message here"
        handleChange={onInputChange}
        value={formState.message}
      />
    )
  };

  const requestedFieldNames = {
    poetry_guide_requests: "requested_poetry_guide",
    requests: "requested_book"
  };

  const submittedViews = {
    already_covered: (
      <div className="request-content-modal__submitted">
        <div className="top-section">
          <div className="modal__title">You&apos;re in luck!</div>
          <div className="modal__subtitle">
            We already have a{" "}
            <a href={coveredResource.url}>
              guide for <i>{coveredResource.title}</i>
            </a>
            .
          </div>
        </div>
      </div>
    ),
    approved_request_exists: (
      <div className="request-content-modal__submitted">
        <div className="top-section">
          <div className="modal__title">That&apos;s already on our list!</div>
          <div className="modal__subtitle">
            We&apos;ve added your vote for <i>{coveredResource.title}</i> and
            now it has {coveredResource.votes}{" "}
            {coveredResource.votes === 1 ? "vote" : "votes"}.
          </div>
        </div>
      </div>
    ),
    dupe_request: (
      <div className="request-content-modal__submitted">
        <div className="top-section">
          <div className="modal__title">
            You&apos;ve already requested <i>{coveredResource.title}</i>.
          </div>
          <div className="modal__subtitle">
            Thanks for letting us know how much you’d like for us to cover it!
          </div>
        </div>
      </div>
    ),
    success: (
      <div className="request-content-modal__submitted">
        <div className="top-section">
          <div className="modal__title">Your request has been submitted!</div>
          <div className="modal__subtitle">{props.successMessage}</div>
        </div>
      </div>
    ),
    unapproved_request_exists: (
      <div className="request-content-modal__submitted">
        <div className="top-section">
          <div className="modal__title">Your request has been submitted!</div>
          <div className="modal__subtitle">
            We’ll let you know once we approve this suggestion so you can vote
            for it!
          </div>
        </div>
      </div>
    )
  };
  return (
    <Modal
      showOnMount={props.showOnMount}
      showAfterDelay={props.showAfterDelay}
      id="request-content-modal"
      footer={<Footer />}
      onHidden={resetForm}
      backdropStatic // Prevents clicks on backdrop from closing modal.
      title={<Title src={props.logoUrl} />}
    >
      {submitted ? (
        submittedViews[submitted]
      ) : (
        <div className="request-content-modal__request">
          <div className="top-section">
            <div className="modal__title">Request a guide</div>
            <div className="modal__subtitle">
              Please enter just one request at a time, and check your spelling
              before submitting!
            </div>
          </div>
          <div className="bottom-section">
            {showError && (
              <div className="lc-banner lc-banner-warning lc-banner-section">
                <ul>
                  <li>
                    There was an error processing your request. Please try
                    again.
                  </li>
                </ul>
              </div>
            )}
            <form
              className="modal__form"
              id="content-request-form"
              onSubmit={onSubmit}
            >
              <ResponsiveSelect
                baseClassName="lc-select-container--md"
                inputId="contact_inquiry_category"
                inputName="contact_inquiry[category]"
                isControlled
                labelText="I'd like to request:"
                largeSelectOptions={categoryOptionsLarge}
                onChange={handleCategoryChange}
                smallSelectOptions={categoryOptionsSmall}
                value={category}
              />
              <AutocompleteInput
                handleChange={onTitleChange}
                labelText="Title of book, play, story, poem..."
                name={requestedFieldNames[category]}
                onSuggestionSelect={onTitleAutoselect}
                suggestionUrl="/contact-inquiry-autocomplete?search_type=title&query="
                value={formState[requestedFieldNames[category]]}
              />
              <AutocompleteInput
                handleChange={onAuthorChange}
                labelText="Author's name"
                name="author_name"
                onSuggestionSelect={onAuthorAutoselect}
                suggestionUrl="/contact-inquiry-autocomplete?search_type=author&query="
                value={formState.author_name}
              />
              {inputs[category]}
            </form>
          </div>
        </div>
      )}
    </Modal>
  );
};

RequestContentModal.propTypes = propTypes;
RequestContentModal.defaultProps = defaultProps;

export default RequestContentModal;
